/**
 * Studio text for imports and exports (board 18, HNV-18-01 to 18-06), in English, the source
 * language. Kept apart from the main file so it can grow on its own; merged under `migration`.
 */
import type { MigrationState } from '../../lib/migration';

export const migrationEn = {
  nav: 'Import history',
  title: 'Import from another tool',
  description:
    'Bring your past support conversations into Hamnivo. Nothing changes in the tool you use now.',
  newImport: 'New import',
  exportHistory: 'Export history',
  loading: 'Loading imports',
  noBrands: 'Add a brand and connect it to Chatwoot before importing.',
  list: {
    title: 'Imports and exports',
    empty: {
      title: 'No imports yet',
      description:
        'Upload an export from tawk.to, Zendesk, Intercom or Hamnivo, or connect another Chatwoot. You see what it holds before anything is imported.',
    },
    columns: {
      file: 'File',
      kind: 'Type',
      brand: 'Brand',
      state: 'Status',
      conversations: 'Conversations',
      started: 'Started',
    },
    open: 'Open {{name}}',
    importKind: 'Import from {{source}}',
    exportKind: 'Export',
    of: '{{done}} of {{total}}',
  },
  back: 'All imports',
  steps: {
    label: 'Import steps',
    source: 'Source',
    sourceHint: 'Export file',
    check: 'Check',
    checkHint: 'Fields and settings',
    sample: 'Review sample',
    sampleHint: 'Check and confirm',
    import: 'Import',
    importHint: 'Run and monitor',
    done: 'Done',
    current: 'Current step',
  },
  sources: {
    'tawk.to': 'tawk.to',
    hamnivo: 'Hamnivo package',
    chatwoot: 'Chatwoot',
    intercom: 'Intercom',
    zendesk: 'Zendesk',
  },
  state: {
    scanning: 'Checking file',
    rejected: 'File refused',
    blocked: 'Needs attention',
    ready: 'Checked',
    dry_running: 'Counting',
    dry_run_ready: 'Ready to import',
    queued: 'Waiting to start',
    running: 'Importing',
    paused: 'Paused',
    cancelled: 'Cancelled',
    completed: 'Finished',
    failed: 'Stopped',
    rolling_back: 'Removing',
    rolled_back: 'Removed',
    closed: 'Approved',
  } satisfies Record<MigrationState, string>,
  upload: {
    title: 'Start an import',
    description: 'Choose where the export comes from and upload it. Nothing is imported yet.',
    brand: 'Import into brand',
    brandRequired: 'Choose a brand.',
    source: 'Where the export comes from',
    sourceHints: {
      'tawk.to': 'The ZIP or JSON files from “Export chats” in tawk.to.',
      zendesk:
        'The ZIP of the full JSON export in Zendesk. Add the users export to the same ZIP to import names.',
      intercom: 'Conversations as the Intercom API returns them, in JSON or NDJSON files.',
      hamnivo: 'A package exported from Hamnivo, or made in the Hamnivo format.',
      chatwoot: 'Another Chatwoot account, read with an access token of one of its users.',
    },
    file: 'Export file',
    fileHint: 'ZIP, JSON or NDJSON, up to 20 MB. It is checked for malware before it is read.',
    fileChosen: 'Chosen: {{name}} ({{size}})',
    fileRequired: 'Choose the export file.',
    fileType: 'Choose a ZIP, JSON or NDJSON file.',
    fileTooLarge: 'This file is {{size}}. Exports can be up to 20 MB.',
    chatwoot: {
      url: 'Chatwoot address',
      urlHint: 'For example https://chat.example.com',
      urlRequired: 'Enter the address of the Chatwoot to import from.',
      account: 'Account ID',
      accountHint: 'The number after /accounts/ in that Chatwoot’s address bar.',
      accountInvalid: 'Enter the account number.',
      token: 'Access token',
      tokenHint:
        'From Profile settings in that Chatwoot. An administrator’s token reads every inbox. It is used once, then deleted.',
      tokenRequired: 'Enter the access token.',
      inboxes: 'Inbox IDs',
      inboxesHint: 'Only these inboxes, separated by commas. Leave empty for all.',
      inboxesInvalid: 'Use inbox numbers separated by commas.',
      submit: 'Connect and check',
    },
    privacy:
      'Only people who manage imports for this brand can see the file. It is deleted once the import is approved or removed.',
    submit: 'Upload and check',
    uploaded: '{{name}} uploaded. Checking it now.',
  },
  file: {
    uploaded: 'Uploaded {{time}} · {{size}}',
    read: 'Read {{time}} · {{size}}',
    into: 'Into {{brand}}',
    purged: 'The file itself was deleted. Counts and reports stay.',
    change: 'Upload another file',
  },
  scanning: {
    title: 'Checking the file',
    description: 'We scan it for malware and read it. Nothing is imported yet.',
    apiTitle: 'Reading from Chatwoot',
    apiDescription:
      'We read the conversations and files, then scan them for malware. Nothing is imported yet.',
  },
  rejected: {
    title: 'This file can’t be imported',
    reasons: {
      malware: 'The malware scanner found a threat in this file. The file was deleted.',
      scan_unavailable: 'The malware scanner could not check the file. Try again in a few minutes.',
      not_supported: 'This is not an export we can read. Choose the ZIP or JSON export file.',
      truncated:
        'The file ends early. It may not have finished downloading. Download the export again.',
      corrupt: 'The file is damaged and can’t be read. Download the export again.',
      too_large: 'The file expands to more than we accept. Split the export into smaller files.',
      too_many_files: 'The archive holds too many files. Split the export into smaller files.',
      unsafe_path: 'The archive contains a file name that is not safe.',
      not_utf8: 'The file is not UTF-8 text.',
      source_denied:
        'The token can’t read this account. Check the account ID, and use the token of one of its administrators.',
      source_unreachable: 'That Chatwoot could not be reached. Check the address and try again.',
      private_address: 'That address is inside a private network, which imports can’t reach.',
      insecure_url: 'The address must start with https://.',
      invalid_url: 'That address is not valid.',
      unresolved: 'That address could not be found.',
      empty: 'The file is empty.',
      other: 'The file could not be read.',
    },
  },
  preflight: {
    title: 'What the export holds',
    description: 'Counts and field names only. Nothing has been imported.',
    counts: {
      conversations: 'Conversations',
      messages: 'Messages',
      internalNotes: 'Internal notes',
      contacts: 'People',
      attachments: 'Files',
      attachmentFiles: 'Files included',
    },
    declared: 'The export says it holds {{conversations}} conversations and {{messages}} messages.',
    truncated: 'Part of the export is missing',
    truncatedHint:
      'Some conversations are incomplete in this export. They are listed below. You can import what is here, or export again first.',
    detected: 'This file looks like an export from {{source}}. Start again and choose {{source}}.',
    checks: {
      title: 'Before importing',
      status: { ok: 'Ready', warning: 'Check', blocked: 'Blocked' },
      codes: {
        authority_connected: {
          ok: 'Chatwoot is connected for this brand.',
          blocked: 'Connect this brand to Chatwoot first (Brands and install).',
        },
        import_inbox: {
          ok: 'Imported conversations go to their own inbox, with no replies, bots, assignment or surveys.',
          will_create:
            'An inbox for imported conversations is created on the first import. It never replies, assigns or sends surveys.',
          blocked:
            'The inbox for imported conversations has settings that could notify people: {{detail}}. Turn them off in Chatwoot, then check again.',
        },
        authority_admin: {
          ok: 'Hamnivo can read the Chatwoot settings it needs to check.',
          blocked: 'Hamnivo can’t read the Chatwoot settings. Reconnect Chatwoot for this brand.',
        },
        automations: {
          ok: 'No Chatwoot automation acts on imported conversations.',
          blocked:
            '{{detail}} Chatwoot automations would act on imported conversations. Turn them off or exclude the import inbox, then check again.',
        },
        integrations: {
          ok: 'No integration receives imported messages.',
          blocked:
            'These integrations would receive imported messages: {{detail}}. Turn them off, then check again.',
        },
        authority_unreachable: {
          ok: 'Chatwoot can be reached.',
          blocked: 'Chatwoot can’t be reached right now. Try again in a few minutes.',
        },
        source_access: {
          ok: 'The token can read every inbox of the account.',
          blocked: 'The token is not an administrator’s: only the inboxes it can see are read.',
        },
        source_mismatch: {
          ok: 'The file matches the source you chose.',
          blocked: 'The file does not match the source you chose.',
        },
      },
      settings: {
        webhook: 'a webhook',
        agent_bot: 'a bot',
        auto_assignment: 'auto-assignment',
        greeting: 'a greeting',
        csat: 'a satisfaction survey',
        working_hours: 'working hours messages',
        single_conversation: 'one conversation per person',
      },
    },
    fields: {
      title: 'Fields',
      description:
        'How each field in the file is imported. Fields that are not imported are listed here, not dropped.',
      columns: {
        source: 'Field in the file',
        target: 'Becomes',
        status: 'Status',
        found: 'Found in',
      },
      status: {
        supported: 'Imported',
        transformed: 'Changed on import',
        unsupported: 'Not imported',
      },
      required: 'Required',
      records: '{{count}} records',
      showUnsupported: 'Show {{fields}} fields that are not imported',
      hideUnsupported: 'Hide fields that are not imported',
      same: 'Kept as it is',
      none: '—',
      targets: {
        'conversation.source_id': 'Conversation ID',
        'conversation.kind': 'Conversation type',
        'conversation.started_at': 'Start time',
        'conversation.completeness_check': 'Completeness check',
        'conversation.attribute': 'Conversation detail',
        'message.original_time': 'Original message time',
        'message.text': 'Message text',
        'message.kind': 'Message type',
        'message.author': 'Sender',
        'message.author_name': 'Sender name',
        'message.author_source_id': 'Sender ID',
        'attachment.link': 'File link',
        'contact.source_id': 'Person ID',
        'contact.name': 'Name',
        'contact.unverified_email': 'Email, kept as not verified',
        'contact.unverified_phone': 'Phone, kept as not verified',
        'contact.location': 'Location',
        'conversation.status': 'Status',
        'conversation.labels': 'Labels',
        'conversation.assignee': 'Assignee',
        'conversation.team': 'Team',
        'message.visibility': 'Private or public',
        'attachment.file': 'File',
        'contact.attribute': 'Person detail',
        'contact.identifier': 'Identifier',
        'conversation.subject': 'Subject',
        'conversation.channel': 'Channel',
        'message.source_id': 'Message ID',
        'message.formatting': 'Formatting',
      },
    },
    missing: {
      title: 'A required field is missing',
      description:
        'This export has no “{{field}}”. Conversations can’t be imported without it. Export again with it included.',
    },
    limits: {
      title: 'Known limits of {{source}}',
      codes: {
        tawk_export_cap:
          'tawk.to exports up to 50 chats at a time. Export every period you need, and import each file.',
        tawk_rest_approval:
          'Importing straight from the tawk.to API needs approval from tawk.to, so imports use exported files.',
        tawk_attachment_links:
          'Exports hold links to files, not the files. Files are listed as not imported.',
        tawk_tickets: 'Tickets and offline messages are not imported.',
        tawk_no_notes: 'tawk.to exports have no internal notes.',
        tawk_unverified_email:
          'Emails were typed in by visitors. They are kept for reference and never used to match people.',
        chatwoot_snapshot_cap:
          'One import reads a set number of conversations, oldest first. Run it again to continue: conversations already imported are not read twice.',
        chatwoot_agent_scope: 'An agent’s token reads only that agent’s inboxes.',
        chatwoot_activity: 'Activity messages (status changes, assignments) are not imported.',
        chatwoot_unverified_contacts:
          'Emails and phone numbers are kept as not verified; people are never matched by them.',
        chatwoot_token_once:
          'The token is used once to read the account, then deleted from Hamnivo. Revoke it in Chatwoot afterwards.',
        zendesk_large_tickets:
          'Zendesk leaves out the comments of tickets over 1 MB. Those tickets are listed as incomplete and are not imported.',
        zendesk_users_export:
          'Add the users export to the same ZIP. It gives names and tells customers from teammates. Without it, comments by unknown people are not imported.',
        zendesk_attachment_links:
          'Exports hold links to files, not the files. Files are listed as not imported.',
        zendesk_history: 'Ticket history, side conversations and custom fields are not imported.',
        zendesk_unverified_contacts:
          'Emails and phone numbers are kept as not verified; people are never matched by them.',
        intercom_api_format:
          'Intercom has no export file with message text. Upload conversations as its API returns them.',
        intercom_parts_cap:
          'Intercom’s API gives at most the latest 500 parts of a conversation. Longer conversations are imported without their oldest parts and listed as incomplete.',
        intercom_events: 'Assignments, state changes and other events are not imported.',
        intercom_plain_text:
          'Messages are imported as plain text. Formatting and inline images are left out.',
        intercom_attachment_links:
          'Conversations hold links to files, not the files. Files are listed as not imported.',
        intercom_unverified_contacts:
          'Emails and phone numbers are kept as not verified; people are never matched by them.',
        package_links_only: 'Files listed only as links are not imported.',
      },
    },
    issues: {
      title: 'Records that need review',
      none: 'Every record can be imported.',
      count: '{{count}} records',
      examples: 'For example: {{ids}}',
      error: 'Can’t be imported',
      warning: 'Check',
    },
    recheck: 'Check again',
    next: 'Review sample',
    blocking: 'Fix the items marked “Blocked”, then check again.',
    cancel: 'Cancel import',
    cancelTitle: 'Cancel this import?',
    cancelBody: 'Nothing was imported. The uploaded file is deleted.',
    cancelled: 'Import cancelled.',
  },
  reasons: {
    duplicate_existing: 'Already imported',
    duplicate_in_file: 'In the file more than once',
    invalid_record: 'Not a valid record',
    missing_time: 'No time',
    unsupported_type: 'Type not supported',
    unsupported_sender: 'Sender not supported',
    missing_conversation: 'Its conversation is missing',
    missing_contact: 'Its person is missing',
    empty_message: 'Nothing to import',
    missing_attachment: 'File missing from the export',
    binary_not_in_export: 'Only a link, the file is not in the export',
    checksum_mismatch: 'File damaged (checksum differs)',
    type_mismatch: 'File type not allowed or not what it claims',
    too_large: 'Too large',
    malware: 'Malware found',
    scan_unavailable: 'Could not be scanned',
    authority_rejected: 'Chatwoot refused it',
    authority_unavailable: 'Chatwoot was unavailable',
    parent_failed: 'Its conversation was not imported',
    not_referenced: 'Not used by any conversation',
    cancelled: 'Import cancelled first',
    truncated: 'Cut short in the export',
    invalid_json: 'Unreadable line',
    truncated_file: 'File cut short',
    count_mismatch: 'Fewer records than the file says',
    export_cap_reached: 'The export may have hit the 50-chat limit',
    incomplete_package: 'The package says it is incomplete',
    no_conversations: 'No conversations found',
    unknown_file: 'File not recognised',
  },
  kinds: {
    conversation: 'Conversations',
    message: 'Messages',
    note: 'Internal notes',
    contact: 'People',
    attachment: 'Files',
  },
  kind: {
    conversation: 'Conversation',
    message: 'Message',
    contact: 'Person',
    attachment: 'File',
  },
  dryRun: {
    title: 'Sample results',
    description: 'What the import would do. Nothing has been changed.',
    counting: 'Counting what the import would do…',
    ready: 'conversations ready',
    readyHint: 'These will be imported.',
    duplicates: 'duplicates',
    duplicatesHint: 'Already imported. They are skipped.',
    review: 'need review',
    reviewHint: 'Missing or invalid data. Listed, not imported.',
    messages: '{{ready}} messages, {{internal}} of them internal notes.',
    people: '{{verified}} people with a verified ID, {{unverified}} without.',
    peopleHint: 'People are never matched by typed-in email or phone.',
    files: '{{ready}} files ready, {{review}} need review.',
    truncated: 'This export is incomplete. The report will say what is missing.',
    duplicateChoice: 'How to handle duplicates',
    duplicateSkip: 'Skip existing',
    duplicateHint: 'Conversations already imported are skipped. The originals stay unchanged.',
    destination:
      'Conversations are imported into {{brand}}. Only people with access to this brand can see them.',
    nothingSent: 'Nothing is sent to customers or teammates while importing.',
    report: 'Download review report',
    run: 'Run import',
    runTitle: 'Import {{count}} conversations into {{brand}}?',
    runBody:
      'Conversations keep their original times, and internal notes stay private. You can pause or cancel at any time.',
    runSubmit: 'Start import',
    started: 'Import started.',
    again: 'Count again',
  },
  sample: {
    title: 'Sample conversation',
    description:
      'One conversation from the file, as it will appear. Emails and numbers are hidden.',
    choose: 'Sample',
    option: 'Conversation {{number}}',
    loading: 'Loading the sample',
    source: 'In the export',
    imported: 'In Hamnivo',
    notYet: 'Not imported yet.',
    started: 'Started {{time}}',
    internal: 'Internal note',
    authors: { customer: 'Customer', agent: 'Teammate', bot: 'Bot', system: 'System' },
    noText: 'No text',
    originalTime: 'Original time {{time}}',
    importedAt: 'Imported {{time}}',
    matches: 'Matches the export',
    differs: 'Differs from the export',
    problems: {
      order_or_count: 'Messages are missing or out of order.',
      original_time: 'A message has a different original time.',
      visibility: 'A message has a different visibility.',
    },
    unavailable: 'Samples are not available: the export file was deleted.',
  },
  progress: {
    title: 'Import progress',
    queued: 'Waiting to start…',
    count: '{{done}} of {{total}} conversations imported',
    percent: '{{percent}} done',
    checkpoint: 'Conversation {{done}} of {{total}}, saved {{time}}',
    closeHint: 'You can close this page. The import continues in the background.',
    pausing: 'Pausing at the next checkpoint…',
    cancelling: 'Cancelling at the next checkpoint…',
    paused: 'Paused at conversation {{done}} of {{total}}.',
    failed: 'The import stopped: {{reason}}. It continues from its checkpoint when you resume.',
    errors: {
      authority_unavailable: 'Chatwoot was unavailable',
      import_inbox_not_quiet: 'the import inbox has settings that could notify people',
      internal: 'something went wrong on our side',
      other: 'something went wrong',
    },
    pause: 'Pause',
    resume: 'Resume',
    cancel: 'Cancel import',
    cancelTitle: 'Cancel this import?',
    cancelBody:
      'It stops at the next checkpoint. Conversations already imported stay until you remove them.',
    pauseRequested: 'The import pauses at the next checkpoint.',
    resumed: 'Import resumed.',
    cancelRequested: 'The import stops at the next checkpoint.',
  },
  result: {
    completed: 'Import completed',
    completedIssues: 'Import completed with some issues',
    cancelled: 'Import cancelled',
    rolledBack: 'Import removed',
    closed: 'Import approved',
    finished: 'Finished {{time}}',
    approved: 'Approved {{time}}',
    imported: 'conversations imported',
    skipped: 'skipped (duplicates)',
    notImported: 'not imported',
    failed: 'failed',
    downloadErrors: 'Download what was not imported',
    retry: 'Retry failed records',
    retryHint: 'Only records that failed are sent again. Imported records are never repeated.',
    retried: 'Retrying the failed records.',
  },
  review: {
    title: 'Reconciliation',
    description: 'Every record in the export, and what became of it.',
    columns: {
      kind: 'Records',
      discovered: 'In the export',
      imported: 'Imported',
      skipped: 'Skipped',
      quarantined: 'Not imported',
      failed: 'Failed',
      pending: 'Not reached',
      rolledBack: 'Removed',
    },
    missingFromExport:
      'Declared by the export but not in it: {{messages}} messages, {{conversations}} conversations.',
    checks: {
      title: 'Checks',
      passed: 'Passed',
      failed: 'Failed',
      codes: {
        all_accounted: 'Every record was imported or has a reason',
        no_failures: 'Nothing failed to import',
        export_complete: 'The export was complete',
        no_duplicates: 'Nothing was imported twice',
        private_kept_private: 'Internal notes stayed private',
        nothing_sent: 'Nothing was sent to customers',
        sample_matches: 'Sample conversations match the export',
        nothing_left: 'Everything the import created was removed',
      },
    },
    reasons: { title: 'Why records were not imported', none: 'Every record was imported.' },
    unsupported: {
      title: 'Fields not imported',
      none: 'Every field was imported.',
      count: '{{count}} records',
    },
    quarantine: {
      title: 'Records not imported',
      description: 'Each with its reason. Download the full list as a spreadsheet.',
      none: 'No records were left out.',
      columns: {
        kind: 'Type',
        id: 'ID in the export',
        conversation: 'Conversation',
        reason: 'Reason',
      },
      more: 'Show more',
      loading: 'Loading records',
    },
    samples: {
      title: 'Compare samples',
      description: 'Conversations read back from Chatwoot, beside the export.',
    },
    cutover: {
      title: 'Approve the import',
      description: 'Check these before you stop using the old tool.',
      checklist: {
        counts: 'The counts match what you expected.',
        samples: 'The sample conversations look right.',
        live: 'New conversations already arrive through Hamnivo, so nothing is imported twice.',
      },
      acknowledge: 'I accept the failed checks listed above.',
      acknowledgeRequired: 'Confirm that you accept the failed checks.',
      approve: 'Approve import',
      approved: 'Import approved.',
      rollbackWindow: 'Until you approve, you can remove everything this import created.',
    },
    rollback: {
      action: 'Remove imported conversations',
      title: 'Remove what this import created?',
      body: 'Conversations and people this import created are deleted from Chatwoot. Live conversations and people used elsewhere are kept.',
      submit: 'Remove',
      started: 'Removing the imported conversations.',
      done: 'Removed {{conversations}} conversations and {{contacts}} people.',
      kept: '{{count}} records were kept because they are still in use.',
    },
  },
  export: {
    title: 'Export history',
    description:
      'Download a brand’s conversations as a Hamnivo package: the same format imports use.',
    brand: 'Brand',
    submit: 'Export',
    queued: 'Export started. It appears in the list when it is ready.',
    running: 'Preparing the package…',
    ready: 'Package ready',
    download: 'Download package',
    expired: 'The package was deleted after 24 hours. Export again.',
    failed: 'The export failed. Try again.',
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
/** Every locale has exactly the English keys; TypeScript reports a missing one. */
export type MigrationTranslation = Strings<typeof migrationEn>;
